import React from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { useNavItems } from '../../shared/hooks/useNavItems';
import { getPrimaryButtonStyles, getSecondaryButtonStyles } from '../../shared/styles';
import { PageHeader } from './PageSection';

/**
 * Shown for an address that does not exist - a typo, an old link, a page that
 * has been switched off.
 *
 * It offers the way on rather than only stating the problem: someone who lands
 * here was looking for something, and the menu is the shortest route back to
 * it. Deliberately not a redirect to the start page, which would leave the
 * visitor wondering what happened and tell search engines the page still
 * exists - build/404.html is served with an actual 404 status.
 */
export const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const lp = useLocalizedPath();
  const navItems = useNavItems().filter((item) => item.path !== '/');

  return (
    <Box>
      <PageHeader title={t('notFound.title')} subtitle={t('notFound.body')}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <Button
            component={Link}
            to={lp('/')}
            sx={getPrimaryButtonStyles(theme)}
            endIcon={<ArrowForward />}
          >
            {t('notFound.home')}
          </Button>
        </Box>

        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {navItems.map((item) => (
            <Box component="li" key={item.key}>
              <Button component={Link} to={lp(item.path)} sx={getSecondaryButtonStyles(theme)}>
                {item.label}
              </Button>
            </Box>
          ))}
        </Box>
      </PageHeader>
    </Box>
  );
};
